/**
 * User Stats Component
 *
 * Displays statistics about tenant users.
 *
 * @fileoverview User statistics display component
 */

import React from 'react';
import {
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Box,
  Skeleton,
  useColorModeValue,
} from '@chakra-ui/react';
import type { TenantUserStats } from '../../types/users';

interface UserStatsProps {
  stats: TenantUserStats | null;
  loading: boolean;
}

/**
 * User Stats Component
 *
 * Displays key metrics about tenant users.
 */
export const UserStats: React.FC<UserStatsProps> = ({ stats, loading }) => {
  const cardBg = useColorModeValue('white', 'xacto.darkSurface');
  const borderColor = useColorModeValue('gray.200', 'xacto.borderGray');
  const labelColor = useColorModeValue('gray.500', 'xacto.silverGray');
  const numberColor = useColorModeValue('gray.900', 'xacto.pureWhite');

  if (loading) {
    return (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        {[1, 2, 3, 4].map((i) => (
          <Box
            key={i}
            bg={cardBg}
            borderRadius="lg"
            border="1px"
            borderColor={borderColor}
            p={4}
          >
            <Skeleton h="20px" w="60px" mb={2} />
            <Skeleton h="32px" w="40px" mb={1} />
            <Skeleton h="16px" w="80px" />
          </Box>
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={4}
      >
        <Stat>
          <StatLabel color={labelColor}>Total Members</StatLabel>
          <StatNumber color={numberColor}>{stats?.total ?? 0}</StatNumber>
          <StatHelpText color={labelColor}>All tenant members</StatHelpText>
        </Stat>
      </Box>

      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={4}
      >
        <Stat>
          <StatLabel color={labelColor}>Active</StatLabel>
          <StatNumber color="green.500">{stats?.active ?? 0}</StatNumber>
          <StatHelpText color={labelColor}>Active accounts</StatHelpText>
        </Stat>
      </Box>

      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={4}
      >
        <Stat>
          <StatLabel color={labelColor}>Admins</StatLabel>
          <StatNumber color="blue.500">{stats?.admins ?? 0}</StatNumber>
          <StatHelpText color={labelColor}>Tenant admins</StatHelpText>
        </Stat>
      </Box>

      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={4}
      >
        <Stat>
          <StatLabel color={labelColor}>Pending Invites</StatLabel>
          <StatNumber color="orange.500">{stats?.pendingInvites ?? 0}</StatNumber>
          <StatHelpText color={labelColor}>Awaiting signup</StatHelpText>
        </Stat>
      </Box>
    </SimpleGrid>
  );
};

export default UserStats;
